import type { SessionCode, SessionUser } from './auth.js';
import { roleCanEditCatalog, roleCanMoveAssets } from './catalog.js';
import { roleCanEditEvents } from './events.js';
import { roleCanInventory } from './inventory.js';
import { roleCanManageReplacements } from './replacements.js';

/** In-app Help index (F-11). Copy lives in client markdown; this file is visibility and mapping. */

export const HELP_SLUGS = [
  'sign-in',
  'scan',
  'inventory',
  'inventory-results',
  'events',
  'event-edit',
  'availability',
  'shipped-via',
  'event-codes',
  'moves',
  'labels',
  'users',
  'replacements',
  'glossary',
] as const;
export type HelpSlug = (typeof HELP_SLUGS)[number];

export const HELP_GROUPS = ['weekend', 'planning', 'shop', 'admin', 'words'] as const;
export type HelpGroup = (typeof HELP_GROUPS)[number];

export const HELP_GROUP_LABELS: Record<HelpGroup, string> = {
  weekend: 'This weekend',
  planning: 'Planning',
  shop: 'Shop',
  admin: 'Admin',
  words: 'Words',
};

/** Event-code sessions see this gym-floor set (plus sign-in). */
export const HELP_GYM_FLOOR_SLUGS: readonly HelpSlug[] = [
  'scan',
  'inventory',
  'inventory-results',
  'events',
];

export const HELP_MISSING_COPY = 'That help page is not in InventoHub.';
export const HELP_FORBIDDEN_COPY = 'That help page is not for this sign-in.';

export const HELP_ROLE_TOKENS = [
  'anyone',
  'signed-in',
  'google',
  'admin',
  'equipment_admin',
  'staff',
  'viewer',
] as const;
export type HelpRoleToken = (typeof HELP_ROLE_TOKENS)[number];

export interface HelpFrontMatter {
  slug: HelpSlug;
  title: string;
  roles: HelpRoleToken[];
  chooserLabel: string | null;
  chooserRoles: HelpRoleToken[] | null;
  group: HelpGroup | null;
}

export interface HelpDocument extends HelpFrontMatter {
  body: string;
  sections: HelpSection[];
}

export interface HelpSection {
  heading: string;
  markdown: string;
}

export interface HelpSearchHit {
  slug: HelpSlug;
  title: string;
  subtitle: string;
  href: string;
}

export interface HelpBrowseGroup {
  id: HelpGroup;
  label: string;
  articles: HelpFrontMatter[];
}

const CHOOSER_ORDER: readonly HelpSlug[] = [
  'sign-in',
  'scan',
  'inventory',
  'inventory-results',
  'events',
  'event-edit',
  'availability',
  'shipped-via',
  'event-codes',
  'moves',
  'labels',
  'users',
  'replacements',
];

const GYM_FLOOR = new Set<HelpSlug>(['sign-in', ...HELP_GYM_FLOOR_SLUGS]);

export function isHelpSlug(value: string): value is HelpSlug {
  return (HELP_SLUGS as readonly string[]).includes(value);
}

export function isHelpGroup(value: string): value is HelpGroup {
  return (HELP_GROUPS as readonly string[]).includes(value);
}

export function isHelpRoleToken(value: string): value is HelpRoleToken {
  return (HELP_ROLE_TOKENS as readonly string[]).includes(value);
}

export function parseRoleTokens(value: string): HelpRoleToken[] {
  const tokens: HelpRoleToken[] = [];
  for (const part of value.split(',')) {
    const token = part.trim();
    if (!token) continue;
    if (!isHelpRoleToken(token)) {
      throw new Error(`Unknown help role token: ${token}`);
    }
    tokens.push(token);
  }
  if (tokens.length === 0) throw new Error('Help roles cannot be empty');
  return tokens;
}

export function parseHelpMarkdown(source: string): HelpDocument {
  const match = source.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);
  if (!match) throw new Error('Help markdown is missing front matter');
  const raw: Record<string, string> = {};
  for (const line of (match[1] ?? '').split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const idx = trimmed.indexOf(':');
    if (idx === -1) throw new Error(`Help front matter line is missing a colon: ${trimmed}`);
    const key = trimmed.slice(0, idx).trim();
    const value = trimmed.slice(idx + 1).trim();
    raw[key] = value;
  }
  const slug = raw.slug ?? '';
  if (!isHelpSlug(slug)) throw new Error(`Unknown help slug: ${slug}`);
  const title = raw.title?.trim() ?? '';
  if (!title) throw new Error(`Help article ${slug} is missing a title`);
  const roles = parseRoleTokens(raw.roles ?? '');
  const chooserLabel = raw.chooserLabel?.trim() ? raw.chooserLabel.trim() : null;
  const chooserRoles = raw.chooserRoles?.trim() ? parseRoleTokens(raw.chooserRoles) : null;
  const groupRaw = raw.group?.trim() ?? '';
  const group = groupRaw ? (isHelpGroup(groupRaw) ? groupRaw : null) : null;
  if (groupRaw && !group) throw new Error(`Unknown help group: ${groupRaw}`);
  const body = (match[2] ?? '').trim();
  return {
    slug,
    title,
    roles,
    chooserLabel,
    chooserRoles,
    group,
    body,
    sections: splitHelpSections(body),
  };
}

export function splitHelpSections(body: string): HelpSection[] {
  const chunks = body.split(/^## /m);
  const sections: HelpSection[] = [];
  for (const chunk of chunks) {
    const trimmed = chunk.trim();
    if (!trimmed) continue;
    const nl = trimmed.search(/\r?\n/);
    if (nl === -1) {
      sections.push({ heading: trimmed, markdown: '' });
      continue;
    }
    const heading = trimmed.slice(0, nl).trim();
    const markdown = trimmed.slice(nl).trim();
    if (!heading) continue;
    sections.push({ heading, markdown });
  }
  return sections;
}

export function sessionCanReadHelp(
  article: HelpFrontMatter,
  user: SessionUser | null,
  code: SessionCode | null,
): boolean {
  if (!user && !code) {
    return article.roles.includes('anyone');
  }
  if (code) return GYM_FLOOR.has(article.slug);
  return roleTokensAllow(article.roles, user);
}

export function sessionCanSeeHelpChooser(
  article: HelpFrontMatter,
  user: SessionUser | null,
  code: SessionCode | null,
): boolean {
  if (!article.chooserLabel) return false;
  if (!sessionCanReadHelp(article, user, code)) return false;
  const tokens = article.chooserRoles ?? article.roles;
  if (!user && !code) return tokens.includes('anyone');
  if (code) return GYM_FLOOR.has(article.slug);
  return roleTokensAllow(tokens, user);
}

function roleTokensAllow(tokens: readonly HelpRoleToken[], user: SessionUser | null): boolean {
  if (tokens.includes('anyone') || tokens.includes('signed-in')) return true;
  if (!user) return false;
  if (tokens.includes('google')) return true;
  return tokens.includes(user.role);
}

export function visibleHelpChooser(
  articles: readonly HelpFrontMatter[],
  user: SessionUser | null,
  code: SessionCode | null,
): HelpFrontMatter[] {
  const allowed = articles.filter((article) => sessionCanSeeHelpChooser(article, user, code));
  return [...allowed].sort((a, b) => chooserIndex(a.slug) - chooserIndex(b.slug));
}

function chooserIndex(slug: HelpSlug): number {
  const index = CHOOSER_ORDER.indexOf(slug);
  return index === -1 ? CHOOSER_ORDER.length : index;
}

export function visibleHelpBrowse(
  articles: readonly HelpFrontMatter[],
  user: SessionUser | null,
  code: SessionCode | null,
): HelpBrowseGroup[] {
  const readable = articles.filter(
    (article) => article.group && sessionCanReadHelp(article, user, code),
  );
  const groups: HelpBrowseGroup[] = [];
  for (const id of HELP_GROUPS) {
    const rows = readable.filter((article) => article.group === id);
    if (rows.length === 0) continue;
    groups.push({ id, label: HELP_GROUP_LABELS[id], articles: rows });
  }
  return groups;
}

export function filterHelpSearch(
  articles: readonly HelpFrontMatter[],
  user: SessionUser | null,
  code: SessionCode | null,
  query: string,
): HelpSearchHit[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  const hits: HelpSearchHit[] = [];
  for (const article of articles) {
    if (!sessionCanReadHelp(article, user, code)) continue;
    const hay = `${article.title} ${article.chooserLabel ?? ''}`.toLowerCase();
    if (!hay.includes(needle)) continue;
    hits.push({
      slug: article.slug,
      title: article.title,
      subtitle:
        article.chooserLabel && article.chooserLabel !== article.title
          ? article.chooserLabel
          : 'Help',
      href: `/help/${article.slug}`,
    });
  }
  return hits.slice(0, 8);
}

/** Header `?` mapping (F-11 contextual table). Unmapped routes go to Help home. */
export function helpHrefForPath(pathname: string): string {
  const slug = helpSlugForPath(pathname);
  return slug ? `/help/${slug}` : '/help';
}

export function helpSlugForPath(pathname: string): HelpSlug | null {
  if (pathname === '/login' || pathname.startsWith('/login/')) return 'sign-in';
  if (pathname === '/inventory' || pathname.startsWith('/inventory/')) return 'inventory';
  if (pathname === '/events/new' || pathname.startsWith('/events/new/')) return 'event-edit';
  if (pathname.startsWith('/events/') && pathname !== '/events/') return 'event-edit';
  if (pathname === '/events' || pathname === '/events/') return 'events';
  if (pathname === '/availability' || pathname.startsWith('/availability/')) return 'availability';
  if (pathname === '/trailers' || pathname.startsWith('/trailers/')) return 'shipped-via';
  if (pathname === '/shipments' || pathname.startsWith('/shipments/')) return 'shipped-via';
  if (pathname === '/admin/users' || pathname.startsWith('/admin/users/')) return 'users';
  if (pathname === '/admin/labels' || pathname.startsWith('/admin/labels/')) return 'labels';
  if (pathname === '/replacements' || pathname.startsWith('/replacements/')) return 'replacements';
  return null;
}

/**
 * Open {screen} links on a playbook. Hide the control when this session cannot use the screen.
 * Staff may read Move gear but must not get an Open into the move form.
 */
export function sessionCanOpenHelpHref(
  href: string,
  user: SessionUser | null,
  code: SessionCode | null,
): boolean {
  const path = href.split('?')[0] ?? href;
  if (path === '/login') return true;
  if (path === '/inventory/new') return Boolean(code || (user && roleCanInventory(user.role)));
  if (path === '/events') return Boolean(user || code);
  if (path === '/events/new') return Boolean(user && roleCanEditEvents(user.role));
  if (path === '/availability') {
    return user?.role === 'admin' || user?.role === 'equipment_admin' || user?.role === 'staff';
  }
  if (path === '/trailers') return Boolean(user);
  if (path === '/admin/users') return user?.role === 'admin';
  if (path === '/admin/labels') return Boolean(user && roleCanEditCatalog(user.role));
  if (path === '/replacements') return Boolean(user && roleCanManageReplacements(user.role));
  if (path === '/assets' || path === '/totes') {
    return Boolean(user && roleCanMoveAssets(user.role));
  }
  if (path.startsWith('/help/')) {
    const slug = path.slice('/help/'.length);
    return isHelpSlug(slug);
  }
  return false;
}

export function helpArticleBySlug(
  articles: readonly HelpFrontMatter[],
  slug: string,
): HelpFrontMatter | null {
  if (!isHelpSlug(slug)) return null;
  return articles.find((article) => article.slug === slug) ?? null;
}
